// src/pages/PKBattle.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  connectSocket,
  getSocket,
  joinPK,
  leavePK,
  startPKLive,
  getPKState,
} from "../socket.js";

import { API_BASE, fetchWithToken } from "../api/api.js";

import { useParams, useNavigate } from "react-router-dom";


export default function PKBattle() {

  const { battleId } = useParams();
  const navigate = useNavigate();


  // ==========================================
  // STATE
  // ==========================================

  const [battle, setBattle] =
    useState(null);

  const [roomState, setRoomState] =
    useState(null);

  const [connected, setConnected] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [secondsLeft, setSecondsLeft] =
    useState(0);

  const [finishing, setFinishing] =
    useState(false);

  // ==========================================
  // PK GIFTS
  // ==========================================

  const [gifts, setGifts] =
    useState([]);

  const [giftsLoading, setGiftsLoading] =
    useState(false);

  const [giftSending, setGiftSending] =
    useState(false);

  const [giftError, setGiftError] =
    useState("");

  const [giftAnimation, setGiftAnimation] =
    useState(null);

  const [coins, setCoins] =
    useState(0);


   const [showWinnerOverlay,   setShowWinnerOverlay] =
  useState(false);

const completionHandledRef = useRef(false);
const finishRequestedRef = useRef(false);

const celebrationParticles = useMemo(
  () =>
    Array.from({ length: 30 }, (_, index) => ({
      id: index,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: `${Math.random() * 1.5}s`,
      emoji:
        index % 3 === 0
          ? "🎉"
          : index % 3 === 1
          ? "✨"
          : "🏆",
    })),
  []
);


  // ==========================================
  // CURRENT USER
  // ==========================================

  const getCurrentUserId = useCallback(() => {

    try {

      const token =
        localStorage.getItem("token");

      if (!token) {
        return null;
      }

      const payload =
        JSON.parse(
          atob(
            token
              .split(".")[1]
              .replace(/-/g, "+")
              .replace(/_/g, "/")
          )
        );

      return (
        payload?.id ||
        payload?._id ||
        payload?.userId ||
        null
      );

    } catch {

      return null;
    }

  }, []);


  const currentUserId =
    getCurrentUserId();


  // ==========================================
  // LOAD REAL PK
  // ==========================================

  const loadBattle = useCallback(
  async () => {
    if (!battleId) {
      setError("PK battle ID is missing");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setError("Please log in to view this PK battle.");
      setLoading(false);
      return;
    }

    try {
      setError("");

      console.log("🥊 Loading PK battle:", {
        battleId,
        url: `${API_BASE}/api/pk/${battleId}`,
      });

      const data = await fetchWithToken(
        `${API_BASE}/api/pk/${battleId}`,
        token
      );

      console.log("🥊 PK battle response:", data);

      if (!data?.success || !data?.battle) {
        throw new Error(
          data?.message || "PK battle not found"
        );
      }

      setBattle(data.battle);

    } catch (error) {
      console.error("Load PK error:", error);

      setError(
        error?.message || "Failed to load PK"
      );

    } finally {
      setLoading(false);
    }
  },
  [battleId]
);

  useEffect(() => {

    loadBattle();

  }, [loadBattle]);


  // ==========================================
  // DETERMINE HOST
  // ==========================================

  const isHostA = useMemo(() => {

    if (!battle || !currentUserId) {
      return false;
    }

    return (
      battle.hostA?._id?.toString() ===
        currentUserId.toString() ||
      battle.hostA?.toString() ===
        currentUserId.toString()
    );

  }, [
    battle,
    currentUserId,
  ]);


  const isHostB = useMemo(() => {

    if (!battle || !currentUserId) {
      return false;
    }

    return (
      battle.hostB?._id?.toString() ===
        currentUserId.toString() ||
      battle.hostB?.toString() ===
        currentUserId.toString()
    );

  }, [
    battle,
    currentUserId,
  ]);


  const isHost =
    isHostA || isHostB;


  // ==========================================
// HOST INFORMATION
// ==========================================

const hostA = battle?.hostA;
const hostB = battle?.hostB;

const hostAName =
  hostA?.name ||
  hostA?.username ||
  "Host A";

const hostBName =
  hostB?.name ||
  hostB?.username ||
  "Host B";

const hostAImage =
  hostA?.profilePic ||
  "/profile/default-profile.png";

const hostBImage =
  hostB?.profilePic ||
  "/profile/default-profile.png";


// ==========================================
// GIFT RECEIVER
// ==========================================

const giftReceiverId = useMemo(() => {

  if (!isHost) {
    return null;
  }

  if (isHostA) {
    return (
      hostB?._id ||
      hostB?.id ||
      hostB ||
      null
    );
  }

  if (isHostB) {
    return (
      hostA?._id ||
      hostA?.id ||
      hostA ||
      null
    );
  }

  return null;

}, [
  isHost,
  isHostA,
  isHostB,
  hostA,
  hostB,
]);


// ==========================================
// LOAD PK GIFTS
// ==========================================

const loadGifts = useCallback(
  async () => {

    const token =
      localStorage.getItem("token");

    if (!token) {
      return;
    }

    try {

      setGiftsLoading(true);
      setGiftError("");

      const data =
        await fetchWithToken(
          `${API_BASE}/api/gifts`,
          token
        );

      if (
        !data?.success ||
        !Array.isArray(data?.gifts)
      ) {
        throw new Error(
          data?.message ||
          "Failed to load gifts"
        );
      }

      setGifts(data.gifts);

      console.log(
        "🎁 PK gifts loaded:",
        data.gifts
      );

    } catch (error) {

      console.error(
        "❌ Load PK gifts error:",
        error
      );

      setGiftError(
        error?.message ||
        "Failed to load gifts"
      );

    } finally {

      setGiftsLoading(false);

    }

  },
  []
);

useEffect(() => {

  loadGifts();

}, [loadGifts]);


const loadCoins = useCallback(async () => {
  const token = localStorage.getItem("token");

  if (!token) {
    return;
  }

  try {
    const data = await fetchWithToken(
      `${API_BASE}/api/wallet`,
      token
    );

    if (!data) {
      return;
    }

    const wallet =
      data.wallet ||
      data.data ||
      data;

    const currentCoins =
      Number(
        wallet?.coins ??
        wallet?.coinBalance ??
        wallet?.points ??
        0
      );

    setCoins(
      Number.isFinite(currentCoins)
        ? currentCoins
        : 0
    );

  } catch (error) {
    console.error(
      "❌ Load PK coin balance error:",
      error
    );
  }
}, []);

useEffect(() => {
  loadCoins();
}, [loadCoins]);



  // ==========================================
  // SOCKET
  // ==========================================

  useEffect(() => {

  if (!battleId) {
    return;
  }

  const socket = connectSocket();

  if (!socket) {
    setError("Socket could not connect");
    return;
  }


  // ==========================================
  // SOCKET CONNECTED
  // ==========================================

  const handleConnect = () => {

    console.log(
      "🥊 PK Socket connected"
    );

    setConnected(true);

    // Automatically join this real PK
    joinPK(battleId);
  };


  // ==========================================
  // SOCKET DISCONNECTED
  // ==========================================

  const handleDisconnect = () => {

    console.log(
      "🔴 PK Socket disconnected"
    );

    setConnected(false);
  };


  // ==========================================
  // ROOM STATE
  // ==========================================

  const handleRoomState = (state) => {

    console.log(
      "🥊 PK room state:",
      state
    );

    setRoomState(state);

  };


  // ==========================================
  // PK STARTED
  // ==========================================

  const handleStarted = (state) => {

    console.log(
      "🚀 PK started:",
      state
    );

    setRoomState((previous) => ({
      ...(previous || {}),
      ...(state || {}),
      started: true,
    }));

    // Refresh MongoDB battle information
    loadBattle();

  };


   
  // ==========================================
  // SCORE UPDATED
  // ==========================================

  const handleScoreUpdate = (data) => {

    console.log(
      "🥊 PK score:",
      data
    );

    setRoomState((previous) => ({
      ...(previous || {}),

      hostAScore:
        data?.hostAScore ??
        previous?.hostAScore ??
        0,

      hostBScore:
        data?.hostBScore ??
        previous?.hostBScore ??
        0,
    }));

  };


  const handleFinished = (data) => {
  console.log("🏆 PK finished:", data);
  console.log("💰 PK reward:", data?.reward);

  // Prevent duplicate processing if the server
  // emits both pk:finished and pk:ended.
  if (completionHandledRef.current) {
    console.log(
      "⚠️ PK completion already handled"
    );
    return;
  }

  completionHandledRef.current = true;
  finishRequestedRef.current = false;

  const finalHostAScore =
    data?.hostAScore ??
    battle?.hostAScore ??
    0;

  const finalHostBScore =
    data?.hostBScore ??
    battle?.hostBScore ??
    0;

  const finalWinner =
    data?.winner ??
    battle?.winner ??
    null;

  const finalReward =
    data?.reward ??
    battle?.reward ??
    null;

  const endedAt =
    data?.endedAt ??
    new Date().toISOString();

  setBattle((previous) => ({
    ...(previous || {}),
    status: "completed",
    endedAt,
    hostAScore: finalHostAScore,
    hostBScore: finalHostBScore,
    winner: finalWinner,
    reward: finalReward,
  }));

  setRoomState((previous) => ({
    ...(previous || {}),
    started: false,
    hostAScore: finalHostAScore,
    hostBScore: finalHostBScore,
    reward: finalReward,
  }));

  setSecondsLeft(0);
  setFinishing(false);
  setShowWinnerOverlay(true);
};


const handlePKEnded = (data) => {
  console.log("🏁 PK ended:", data);
  console.log("💰 PK reward:", data?.reward);

  handleFinished(data);
};


// ==========================================
// PK GIFT RECEIVED
// ==========================================

const handleGiftReceived = (data) => {

  console.log(
    "🎁 PK gift received:",
    data
  );

  if (
    data?.hostAScore !== undefined ||
    data?.hostBScore !== undefined
  ) {

    setRoomState((previous) => ({
      ...(previous || {}),

      hostAScore:
        data?.hostAScore ??
        previous?.hostAScore ??
        0,

      hostBScore:
        data?.hostBScore ??
        previous?.hostBScore ??
        0,
    }));

  }

  setGiftAnimation({
    ...data,

    animationId:
      `${Date.now()}-${Math.random()}`,
  });

};



// ==========================================
// PK GIFT SENT
// ==========================================

const handleGiftSent = (data) => {

  console.log(
    "🎁 PK gift sent:",
    data
  );

  if (
    data?.balanceAfter !== undefined
  ) {

    setCoins(
      Number(data.balanceAfter) || 0
    );

  }

  if (
    data?.hostAScore !== undefined ||
    data?.hostBScore !== undefined
  ) {

    setRoomState((previous) => ({
      ...(previous || {}),

      hostAScore:
        data?.hostAScore ??
        previous?.hostAScore ??
        0,

      hostBScore:
        data?.hostBScore ??
        previous?.hostBScore ??
        0,
    }));

  }

  setGiftSending(false);

};



  // ==========================================
  // PK ERROR
  // ==========================================

  const handleError = (data) => {

    console.error(
      "❌ PK Socket error:",
      data
    );

    const message =
      typeof data === "string"
        ? data
        : data?.message ||
          data?.error ||
          "PK socket error";

    setError(message);

    setFinishing(false);
  };



  // ==========================================
  // REGISTER LISTENERS
  // ==========================================

  socket.on(
    "connect",
    handleConnect
  );

  socket.on(
    "disconnect",
    handleDisconnect
  );

  socket.on(
    "pk:room-state",
    handleRoomState
  );

  socket.on(
    "pk:started",
    handleStarted
  );

  socket.on(
    "pk:score-updated",
    handleScoreUpdate
  );

  socket.on(
    "pk:finished",
    handleFinished
  );


   socket.on(
    "pk:gift-received",
    handleGiftReceived
  );

  socket.on(
    "pk:gift-sent",
    handleGiftSent
  );


 
  socket.on(
  "pk:ended",
  handlePKEnded
);

  socket.on(
    "pk:error",
    handleError
  );


  // ==========================================
  // ALREADY CONNECTED
  // ==========================================

  if (socket.connected) {

    setConnected(true);

    joinPK(battleId);

  }


  // ==========================================
  // CLEANUP
  // ==========================================

  return () => {

    socket.off(
      "connect",
      handleConnect
    );

    socket.off(
      "disconnect",
      handleDisconnect
    );

    socket.off(
      "pk:room-state",
      handleRoomState
    );

    socket.off(
      "pk:started",
      handleStarted
    );

    socket.off(
      "pk:score-updated",
      handleScoreUpdate
    );

    socket.off(
      "pk:finished",
      handleFinished
    );


        socket.off(
      "pk:gift-received",
      handleGiftReceived
    );

    socket.off(
      "pk:gift-sent",
      handleGiftSent
    );

    socket.off(
  "pk:ended",
  handlePKEnded
);

    socket.off(
      "pk:error",
      handleError
    );

  };

}, [
  battleId,
  loadBattle,
]);


// ==========================================
// FINISH PK BATTLE
// ==========================================

const finishBattle = useCallback(() => {
  if (!battleId) {
    return;
  }

  if (!isHost) {
    console.log("⚠️ Only a PK host can finish the battle");
    return;
  }

  if (completionHandledRef.current) {
    console.log("⚠️ PK already completed");
    return;
  }

  if (finishRequestedRef.current) {
    console.log("⚠️ PK finish already requested");
    return;
  }

  const socket = getSocket();

  if (!socket?.connected) {
    console.log("⚠️ Cannot finish PK: socket disconnected");
    finishRequestedRef.current = false;
    setFinishing(false);
    return;
  }

  finishRequestedRef.current = true;
  setFinishing(true);
  setError("");

  console.log("🏁 Requesting PK finish:", {
    battleId,
  });

  socket.emit("pk:finish", {
    battleId,
  });
}, [
  battleId,
  isHost,
]);


// ==========================================
// SCORE
// ==========================================

const hostAScore =
  roomState?.hostAScore ??
  battle?.hostAScore ??
  0;

const hostBScore =
  roomState?.hostBScore ??
  battle?.hostBScore ??
  0;


  // ==========================================
  // STARTED
  // ==========================================

  const isStarted =
    roomState?.started ??
    battle?.status === "active";


  // ==========================================
  // START TIME
  // ==========================================

  useEffect(() => {

    if (!isStarted) {

      setSecondsLeft(0);

      return;
    }

    const startedAt =
      roomState?.startedAt ||
      battle?.startedAt;

    if (!startedAt) {
      return;
    }

    const duration =
      Number(
        battle?.duration || 300
      );


    const updateTimer = () => {

      const start =
        new Date(
          startedAt
        ).getTime();

      const end =
        start +
        duration * 1000;

      const remaining =
        Math.max(
          0,
          Math.ceil(
            (end - Date.now()) /
              1000
          )
        );

      setSecondsLeft(
        remaining
      );

    };


    updateTimer();

    const timer =
      setInterval(
        updateTimer,
        1000
      );


    return () => {

      clearInterval(
        timer
      );

    };

  }, [
    isStarted,
    roomState?.startedAt,
    battle?.startedAt,
    battle?.duration,
  ]);


    // ==========================================
  // AUTO FINISH TIMER
  // ==========================================

  useEffect(() => {
    if (
      !isStarted ||
      secondsLeft !== 0 ||
      !isHost ||
      battle?.status !== "active"
    ) {
      return;
    }

    if (
      finishRequestedRef.current ||
      completionHandledRef.current
    ) {
      return;
    }

    finishBattle();
  }, [
    secondsLeft,
    isStarted,
    isHost,
    battle?.status,
    finishBattle,
  ]);




  // ==========================================
  // PK UI HELPERS
  // ==========================================

  const totalDuration =
    Number(battle?.duration || 300);

  const scoreTotal =
    hostAScore + hostBScore;

  const hostAPercentage =
    scoreTotal > 0
      ? Math.round((hostAScore / scoreTotal) * 100)
      : 50;

  const hostBPercentage =
    scoreTotal > 0
      ? Math.round((hostBScore / scoreTotal) * 100)
      : 50;

  const isFinalCountdown =
    isStarted &&
    secondsLeft <= 10;

  const leadingHost =
    hostAScore > hostBScore
      ? "A"
      : hostBScore > hostAScore
      ? "B"
      : "DRAW";


    // ==========================================
// PK REWARD
// ==========================================

const reward =
  roomState?.reward ??
  battle?.reward ??
  null;

const winnerReward =
  Number(
    reward?.winnerReward || 0
  );

const creatorRewardPool =
  Number(
    reward?.creatorRewardPool || 0
  );

const totalCoinsSpent =
  Number(
    reward?.totalCoinsSpent || 0
  );

const platformFee =
  Number(
    reward?.platformFee || 0
  );

const rewardReference =
  reward?.reference || null;


  // ==========================================
  // FORMAT TIMER
  // ==========================================

  const formattedTime = () => {

    const minutes =
      Math.floor(
        secondsLeft / 60
      );

    const seconds =
      secondsLeft % 60;

    return `${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(
      2,
      "0"
    )}`;

  };


  // ==========================================
  // START PK
  // ==========================================

  const handleStart = () => {

    setError("");

    if (!connected) {

      setError(
        "Socket is not connected"
      );

      return;
    }

    if (!isHost) {

      setError(
        "Only a PK host can start the battle"
      );

      return;
    }

completionHandledRef.current = false;
finishRequestedRef.current = false;
setShowWinnerOverlay(false);

    startPKLive(
      battleId
    );

  };


     // ==========================================
  // SEND PK GIFT
  // ==========================================

  const handleSendGift = (
    gift
  ) => {

    setGiftError("");


    if (giftSending) {
  return;
}

    const socket =
      getSocket();

    if (!socket?.connected) {

      setGiftError(
        "Socket is not connected"
      );

      return;
    }

    if (!isStarted) {

      setGiftError(
        "PK has not started"
      );

      return;
    }

    if (!isHost) {

      setGiftError(
        "Only PK hosts can send gifts"
      );

      return;
    }

    if (!giftReceiverId) {

      setGiftError(
        "PK opponent not found"
      );

      return;
    }

    if (!gift?._id) {

      setGiftError(
        "Invalid gift"
      );

      return;
    }

    const cost =
      Number(gift.coinCost);

    if (
      !Number.isFinite(cost) ||
      cost <= 0
    ) {

      setGiftError(
        "Invalid gift cost"
      );

      return;
    }

    if (
      Number(coins) < cost
    ) {

      setGiftError(
        "Not enough coins"
      );

      return;
    }

    setGiftSending(true);

    socket.emit(
      "pk:gift",
      {
        battleId,

        receiverId:
          giftReceiverId,

        giftId:
          gift._id,
      }
    );

  };



    

  // ==========================================
  // SCORE
  // ==========================================

  const handleScore = (
    points
  ) => {

    setError("");

    const socket =
      getSocket();

    if (!socket?.connected) {

      setError(
        "Socket is not connected"
      );

      return;
    }

    if (!isStarted) {

      setError(
        "PK has not started"
      );

      return;
    }

    if (!isHost) {

      setError(
        "You are not a host of this PK"
      );

      return;
    }


    socket.emit(
      "pk:score",
      {
        battleId,
        points,
      }
    );

  };


  
  // ==========================================
  // LEAVE
  // ==========================================

  const handleLeave = () => {

    leavePK(
      battleId
    );

    navigate(
      "/"
    );

  };


  // ==========================================
  // WINNER
  // ==========================================

  const winnerId =
    battle?.winner?._id ||
    battle?.winner;


  const winnerName =
    winnerId &&
    winnerId.toString() ===
      (
        hostA?._id ||
        hostA
      )?.toString()
      ? hostAName
      : winnerId
      ? hostBName
      : null;


  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-3">
            🥊
          </div>

          <p>
            Loading PK battle...
          </p>
        </div>
      </div>
    );

  }


  // ==========================================
  // ERROR WITHOUT BATTLE
  // ==========================================

  if (!battle) {

    return (
      <div className="min-h-screen flex items-center justify-center p-6">

        <div className="text-center">

          <div className="text-5xl mb-4">
            ❌
          </div>

          <h2 className="text-xl font-bold">
            PK unavailable
          </h2>

          <p className="text-red-500 mt-2">
            {error || "PK battle not found"}
          </p>

          <button
            onClick={() => navigate(-1)}
            className="mt-5 px-5 py-2 rounded-lg bg-gray-900 text-white"
          >
            Go Back
          </button>

        </div>

      </div>
    );

  }


  // ==========================================
  // UI
  // ==========================================

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4">

      <div className="max-w-5xl mx-auto">


        {/* HEADER */}

        <div className="flex items-center justify-between mb-6">

          <div>

            <h1 className="text-2xl font-bold">
              🥊 PK Battle
            </h1>

            <p className="text-sm text-gray-400">
              {connected
                ? "🟢 Live connection"
                : "🔴 Connecting..."}
            </p>

          </div>


          <div className="flex items-center gap-2">

  <button
    onClick={() =>
      navigate("/pk/history")
    }
    className="
      px-4
      py-2
      rounded-lg
      bg-gray-800
      hover:bg-gray-700
      font-medium
    "
  >
    📜 History
  </button>

  <button
    onClick={() =>
      navigate("/pk/stats")
    }
    className="
      px-4
      py-2
      rounded-lg
      bg-gray-800
      hover:bg-gray-700
      font-medium
    "
  >
    📊 Stats
  </button>

  <button
    onClick={handleLeave}
    className="
      px-4
      py-2
      rounded-lg
      bg-gray-800
      hover:bg-gray-700
      font-medium
    "
  >
    Leave
  </button>

</div>

        </div>


                {/* BATTLE */}

        <div className="rounded-3xl bg-gray-900 border border-gray-800 overflow-hidden shadow-2xl">

          {/* STATUS */}

          <div className="text-center p-5 border-b border-gray-800">

            {isStarted ? (

              <div className="flex flex-col items-center">

                <div
                  className={`inline-flex items-center gap-2 px-5 py-2 rounded-full ${
                    isFinalCountdown
                      ? "bg-red-600/30 text-red-300 animate-pulse"
                      : "bg-red-600/20 text-red-400"
                  }`}
                >

                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />

                  LIVE PK

                </div>

                <div
                  className={`text-5xl sm:text-6xl font-black mt-4 tracking-wider ${
                    isFinalCountdown
                      ? "text-red-400 animate-pulse"
                      : "text-white"
                  }`}
                >
                  {formattedTime()}
                </div>

                {isFinalCountdown && (
                  <p className="mt-2 text-red-400 text-sm font-semibold">
                    🔥 FINAL COUNTDOWN!
                  </p>
                )}

              </div>

            ) : battle.status === "completed" ? (

  <div>

    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-yellow-500/20 text-yellow-400">
      🏆 PK ENDED
    </div>

    <p className="text-gray-400 text-sm mt-3">
      Final score
    </p>

    {/* PK REWARD */}

    {reward && (
      <div className="mt-5 mx-auto max-w-sm">

        <div className="rounded-2xl bg-green-500/10 border border-green-500/20 p-4">

          <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
            🏆 Winner Reward
          </p>

          <p className="text-3xl font-black text-green-400 mt-1">
            ₦{winnerReward.toLocaleString()}
          </p>

          {totalCoinsSpent > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {totalCoinsSpent.toLocaleString()} coins spent
            </p>
          )}

          {rewardReference && (
            <p className="text-[10px] text-gray-600 mt-2 break-all">
              {rewardReference}
            </p>
          )}

        </div>

      </div>
    )}

  </div>

) : (

              <div>


           {/* REWARD */}

{reward && (
  <div className="mt-6 rounded-2xl bg-green-500/10 border border-green-500/20 p-5">

    <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">
      💰 Winner Reward
    </p>

    <p className="text-4xl font-black text-green-400 mt-2">
      ₦{winnerReward.toLocaleString()}
    </p>

    {totalCoinsSpent > 0 && (
      <p className="text-xs text-gray-500 mt-2">
        From {totalCoinsSpent.toLocaleString()} coins spent
      </p>
    )}

  </div>
)}



                <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-500/20 text-blue-400">
                  ⏳ WAITING
                </div>

                <p className="text-gray-400 text-sm mt-3">
                  Waiting for the battle to begin
                </p>

              </div>

            )}

          </div>


          {/* SCOREBOARD */}

          <div className="relative px-4 sm:px-8 pt-8 pb-6">

            {/* VS */}

            <div className="absolute left-1/2 top-10 -translate-x-1/2 z-10">

              <div className="w-12 h-12 rounded-full bg-gray-950 border-2 border-gray-700 flex items-center justify-center shadow-xl">

                <span className="font-black text-gray-300 text-sm">
                  VS
                </span>

              </div>

            </div>


            <div className="grid grid-cols-2 gap-3 sm:gap-8">

              {/* HOST A */}

              <div
                className={`relative rounded-3xl p-4 sm:p-6 text-center border transition ${
                  leadingHost === "A"
                    ? "border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/10"
                    : "border-gray-800 bg-gray-950/40"
                }`}
              >

                {leadingHost === "A" && (
                  <div className="absolute top-3 left-3 text-xs font-bold text-blue-400">
                    🔥 LEADING
                  </div>
                )}

                <div className="relative inline-block">

                  <img
                    src={hostAImage}
                    alt={hostAName}
                    className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mx-auto border-4 ${
                      leadingHost === "A"
                        ? "border-blue-400"
                        : "border-blue-600"
                    }`}
                  />

                  {isHostA && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                      YOU
                    </div>
                  )}

                </div>

                <h2 className="font-bold text-lg sm:text-xl mt-4 truncate">
                  {hostAName}
                </h2>

                <p className="text-xs text-blue-400 font-semibold mt-1">
                  HOST A
                </p>

                <div className="text-5xl sm:text-6xl font-black mt-5">
                  {hostAScore.toLocaleString()}
                </div>

              </div>


              {/* HOST B */}

              <div
                className={`relative rounded-3xl p-4 sm:p-6 text-center border transition ${
                  leadingHost === "B"
                    ? "border-pink-500 bg-pink-500/10 shadow-lg shadow-pink-500/10"
                    : "border-gray-800 bg-gray-950/40"
                }`}
              >

                {leadingHost === "B" && (
                  <div className="absolute top-3 right-3 text-xs font-bold text-pink-400">
                    LEADING 🔥
                  </div>
                )}

                <div className="relative inline-block">

                  <img
                    src={hostBImage}
                    alt={hostBName}
                    className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mx-auto border-4 ${
                      leadingHost === "B"
                        ? "border-pink-400"
                        : "border-pink-600"
                    }`}
                  />

                  {isHostB && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-pink-600 text-white text-[10px] font-bold">
                      YOU
                    </div>
                  )}

                </div>

                <h2 className="font-bold text-lg sm:text-xl mt-4 truncate">
                  {hostBName}
                </h2>

                <p className="text-xs text-pink-400 font-semibold mt-1">
                  HOST B
                </p>

                <div className="text-5xl sm:text-6xl font-black mt-5">
                  {hostBScore.toLocaleString()}
                </div>

              </div>

            </div>


            {/* SCORE BAR */}

            <div className="mt-8">

              <div className="flex justify-between text-xs font-semibold mb-2">

                <span className="text-blue-400">
                  {hostAPercentage}%
                </span>

                <span className="text-gray-500">
                  SCORE
                </span>

                <span className="text-pink-400">
                  {hostBPercentage}%
                </span>

              </div>

              <div className="h-4 rounded-full overflow-hidden bg-gray-800 flex">

                <div
                  className="h-full bg-blue-500 transition-all duration-500"
                  style={{
                    width: `${hostAPercentage}%`,
                  }}
                />

                <div
                  className="h-full bg-pink-500 transition-all duration-500"
                  style={{
                    width: `${hostBPercentage}%`,
                  }}
                />

              </div>

            </div>

          </div>


          {/* SCORE CONTROLS */}

          {isStarted && isHost && (

            <div className="p-5 sm:p-6 border-t border-gray-800 bg-gray-950/40">

              <p className="text-center text-sm text-gray-400 mb-4">

                You are{" "}

                <strong className="text-white">
                  {isHostA ? "Host A" : "Host B"}
                </strong>

              </p>

              <div className="grid grid-cols-3 gap-2 sm:gap-3">

                <button
                  onClick={() => handleScore(100)}
                  className="py-3 sm:py-4 rounded-xl bg-green-600 font-bold hover:bg-green-500 active:scale-95 transition"
                >
                  +100
                </button>

                <button
                  onClick={() => handleScore(500)}
                  className="py-3 sm:py-4 rounded-xl bg-green-600 font-bold hover:bg-green-500 active:scale-95 transition"
                >
                  +500
                </button>

                <button
                  onClick={() => handleScore(1000)}
                  className="py-3 sm:py-4 rounded-xl bg-green-600 font-bold hover:bg-green-500 active:scale-95 transition"
                >
                  +1,000
                </button>

              </div>

            </div>

          )}


          {/* START */}

          {!isStarted &&
            battle.status === "pending" &&
            isHost && (

              <div className="p-6 border-t border-gray-800 text-center bg-gray-950/30">

                <button
                  onClick={handleStart}
                  disabled={!connected}
                  className="px-10 py-4 rounded-2xl bg-red-600 font-bold text-lg hover:bg-red-500 active:scale-95 transition disabled:opacity-50"
                >
                  🚀 Start PK
                </button>

                <p className="text-xs text-gray-500 mt-3">
                  Both players can enter the battle before starting.
                </p>

              </div>

            )}


          {/* WAITING */}

          {!isStarted &&
            battle.status === "pending" &&
            !isHost && (

              <div className="p-6 border-t border-gray-800 text-center text-gray-400">

                <div className="text-3xl mb-2">
                  ⏳
                </div>

                <p className="font-semibold text-gray-300">
                  Waiting for a host to start the PK
                </p>

                <p className="text-xs mt-1">
                  The battle will begin when the host starts it.
                </p>

              </div>

            )}


          {/* RESULT */}

          {battle.status === "completed" && (

            <div className="p-8 border-t border-gray-800 text-center bg-gray-950/40">

              {winnerName ? (

                <>
                  <div className="text-6xl mb-4 animate-bounce">
                    🏆
                  </div>

                  <p className="text-yellow-400 text-sm font-bold uppercase tracking-wider">
                    Winner
                  </p>

                  <h2 className="text-3xl sm:text-4xl font-black mt-2">
                    {winnerName}
                  </h2>

                  <p className="text-gray-400 mt-3">
                    Congratulations on winning the PK battle!
                  </p>
                </>

              ) : (

                <>
                  <div className="text-6xl mb-4">
                    🤝
                  </div>

                  <h2 className="text-3xl font-black">
                    It's a Draw!
                  </h2>

                  <p className="text-gray-400 mt-2">
                    Both players finished with the same score.
                  </p>
                </>

              )}

            </div>

          )}

        </div>

        {/* CONNECTION / ERROR */}

        {error && (

          <div className="mt-4 p-4 rounded-xl bg-red-950 border border-red-800 text-red-300">
            ❌ {error}
          </div>

        )}


        {/* DEBUG INFO */}

        <div className="mt-5 p-4 rounded-xl bg-gray-900 border border-gray-800 text-xs text-gray-500">

          <div>
            Battle: {battleId}
          </div>

          <div>
            Your ID: {currentUserId || "Not detected"}
          </div>

          <div>
            Role:{" "}
            {isHostA
              ? "Host A"
              : isHostB
              ? "Host B"
              : "Viewer"}
          </div>

          <div>
            Room users:{" "}
            {roomState?.users?.length || 0}
          </div>

        </div>

      </div>


     {showWinnerOverlay && (
  <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6">

    <div className="relative w-full max-w-md text-center">

      {/* Celebration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">

        {celebrationParticles.map((particle) => (
  <span
    key={particle.id}
    className="absolute text-2xl animate-bounce"
    style={{
      left: particle.left,
      top: particle.top,
      animationDelay: particle.delay,
    }}
  >
    {particle.emoji}
  </span>
))}

      </div>

      {/* Result Card */}
      <div className="relative rounded-[2rem] bg-gray-900 border border-yellow-500/30 shadow-2xl p-8">

        <div className="text-6xl mb-3 animate-bounce">
          🏆
        </div>

        <p className="text-yellow-400 uppercase tracking-[0.3em] text-sm font-bold">
          PK Battle Complete
        </p>

        {winnerName ? (
          <>
            <h1 className="text-4xl font-black mt-3">
              {winnerName}
            </h1>

            <p className="text-xl text-yellow-400 font-bold mt-2">
              🎉 WINNER!
            </p>
          </>
        ) : (
          <>
            <h1 className="text-4xl font-black mt-3">
              DRAW
            </h1>

            <p className="text-gray-400 mt-2">
              What a battle!
            </p>
          </>
        )}

        {/* Winner Avatar */}
        {winnerName && (
          <div className="mt-6 flex justify-center">

            <div className="relative">

              <div className="absolute -inset-3 rounded-full bg-yellow-400/30 animate-ping" />

              <img
                src={
                  winnerId?.toString() ===
                  hostA?._id?.toString()
                    ? hostAImage
                    : hostBImage
                }
                alt={winnerName}
                className="relative w-32 h-32 rounded-full object-cover border-4 border-yellow-400 shadow-xl"
              />

            </div>

          </div>
        )}

        {/* Final Score */}
        <div className="grid grid-cols-3 items-center gap-3 mt-8">

          <div className="rounded-2xl bg-blue-500/10 border border-blue-500/20 p-4">

            <p className="text-sm text-gray-400 truncate">
              {hostAName}
            </p>

            <p className="text-3xl font-black text-blue-400">
              {hostAScore}
            </p>

          </div>

          <div className="text-gray-500 font-black">
            VS
          </div>

          <div className="rounded-2xl bg-pink-500/10 border border-pink-500/20 p-4">

            <p className="text-sm text-gray-400 truncate">
              {hostBName}
            </p>

            <p className="text-3xl font-black text-pink-400">
              {hostBScore}
            </p>

          </div>

        </div>

        {/* Continue */}
        <button
          onClick={() => {
            setShowWinnerOverlay(false);
            navigate("/");
          }}
          className="w-full mt-8 py-4 rounded-2xl bg-yellow-500 hover:bg-yellow-400 text-black font-black text-lg transition active:scale-[0.98]"
        >
          Continue
        </button>

      </div>

    </div>

  </div>
)}

    </div>
  );
}
